import { Card, CardContent, Typography, Divider, Grid as Grid, Paper, Box, IconButton, Button } from "@mui/material";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useTheme } from "@mui/material/styles";

interface EventResultCardProps {
    title: string;

    eventCode: string | number;
    eventId: number | null;
    onEdit: (id: number) => void;

    hmiEventText: string;

    declaration: string;
    snippet: string;

    copiedDeclaration: boolean;
    copiedSnippet: boolean;

    onCopyDeclaration: () => void;
    onCopySnippet: () => void;
}

export default function EventResultCard({
    title,
    eventCode,
    eventId,
    onEdit,
    hmiEventText,
    declaration,
    snippet,
    copiedDeclaration,
    copiedSnippet,
    onCopyDeclaration,
    onCopySnippet
}: EventResultCardProps) {

    const theme = useTheme();

    return (
        <Card elevation={3}>
            <CardContent>
                <Typography sx={{ color: theme.palette.text.primary }} variant="h6" gutterBottom>
                    {title}
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={1}>
                    <Grid size={{ xs: 2.5 }} sx={{ display: "flex", alignItems: "center" }}>
                        <Typography sx={{ color: theme.palette.text.primary }}>Event Code:</Typography>
                    </Grid>

                    <Grid size={{ xs: 1 }} sx={{ display: "flex", alignItems: "center" }}>
                        <Typography sx={{ color: theme.palette.text.primary }}>{eventCode}</Typography>
                    </Grid>

                    <Grid size={{ xs: 8.5 }} sx={{ display: "flex", alignItems: "center" }}>
                        <Button
                            variant="outlined"
                            endIcon={<ModeEditIcon />}
                            onClick={() => eventId != null && eventId > 0 && onEdit(eventId)}
                            color="secondary"
                        >
                            Edit event
                        </Button>
                    </Grid>

                    <Grid size={{ xs: 2.5 }}>
                        <Typography sx={{ color: theme.palette.text.primary }}>HMI Event-Text:</Typography>
                    </Grid>

                    <Grid size={{ xs: 9.5 }}>
                        <Typography sx={{ color: theme.palette.text.primary }}>
                            {hmiEventText}
                        </Typography>
                    </Grid>
                </Grid>

                <Box sx={{ position: "relative", mt: 3 }}>
                    {/* DECLARATION */}
                    <Typography gutterBottom>Declaration (VAR_EXTERNAL):</Typography>

                    <Paper
                        elevation={0}
                        sx={{
                            backgroundColor: theme.palette.action.hover,
                            color: theme.palette.text.primary,
                            p: 2,
                            pr: 3,
                            mb: 2,
                            borderRadius: 2,
                            fontFamily: "Consolas, monospace",
                            whiteSpace: "pre-wrap",
                            overflowX: "auto",
                            borderLeft: `4px solid ${theme.palette.success.main}`,
                            position: "relative",
                        }}
                    >
                        <IconButton
                            onClick={onCopyDeclaration}
                            sx={{
                                position: "absolute",
                                top: 10,
                                right: 10,
                                p: 0,
                                color: theme.palette.text.primary,
                                "&:hover": { backgroundColor: theme.palette.action.focus },
                                width: 22,
                                height: 22,
                            }}
                        >
                            {copiedDeclaration
                                ? <CheckIcon sx={{ color: theme.palette.success.main }} />
                                : <ContentCopyIcon sx={{ color: theme.palette.primary.main }} />
                            }
                        </IconButton>
                        {declaration}
                    </Paper>

                    {/* SNIPPET */}
                    <Typography gutterBottom>Call (adapt as necessary):</Typography>

                    <Paper
                        elevation={0}
                        sx={{
                            backgroundColor: theme.palette.action.hover,
                            color: theme.palette.text.primary,
                            p: 2,
                            borderRadius: 2,
                            fontFamily: "Consolas, monospace",
                            whiteSpace: "pre-wrap",
                            overflowX: "auto",
                            borderLeft: `4px solid ${theme.palette.success.main}`,
                            position: "relative",
                        }}
                    >
                        <IconButton
                            onClick={onCopySnippet}
                            sx={{
                                position: "absolute",
                                top: 10,
                                right: 10,
                                p: 0,
                                color: theme.palette.text.primary,
                                "&:hover": { backgroundColor: theme.palette.action.focus },
                                width: 22,
                                height: 22,
                            }}
                        >
                            {copiedSnippet
                                ? <CheckIcon sx={{ color: theme.palette.success.main }} />
                                : <ContentCopyIcon sx={{ color: theme.palette.primary.main }} />
                            }
                        </IconButton>

                        {snippet}
                    </Paper>
                </Box>
            </CardContent>
        </Card>
    );
}